import axios from 'axios';
import type { ApprovalRequest, AuditEntry, AgentStatus, PolicyRule, Session } from '../types';

const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: { 'Content-Type': 'application/json' },
});

export const approvalsApi = {
  async list(): Promise<ApprovalRequest[]> {
    const { data } = await api.get<{ approvals: ApprovalRequest[] }>('/approvals');
    return data.approvals;
  },

  async approve(id: string, comment?: string): Promise<void> {
    await api.post(`/approvals/${id}/approve`, { comment });
  },

  async reject(id: string, reason: string): Promise<void> {
    await api.post(`/approvals/${id}/reject`, { reason });
  },
};

export const auditApi = {
  async list(params?: {
    startDate?: Date;
    endDate?: Date;
    sessionId?: string;
    limit?: number;
  }): Promise<AuditEntry[]> {
    const { data } = await api.get<{ entries: AuditEntry[] }>('/audit', {
      params,
    });
    return data.entries;
  },

  async getSession(sessionId: string): Promise<Session> {
    const { data } = await api.get<{ session: Session }>(`/audit/sessions/${sessionId}`);
    return data.session;
  },
};

export const agentApi = {
  async getStatus(): Promise<AgentStatus> {
    const { data } = await api.get<{ status: AgentStatus }>('/agent/status');
    return data.status;
  },

  async getSessions(limit = 10): Promise<Session[]> {
    const { data } = await api.get<{ sessions: Session[] }>('/agent/sessions', {
      params: { limit },
    });
    return data.sessions;
  },
};

export const policyApi = {
  async list(): Promise<PolicyRule[]> {
    const { data } = await api.get<{ rules: PolicyRule[] }>('/policies');
    return data.rules;
  },

  async update(id: string, yamlContent: string): Promise<void> {
    await api.put(`/policies/${id}`, { yaml: yamlContent });
  },

  async validate(yamlContent: string): Promise<{ valid: boolean; errors?: string[] }> {
    const { data } = await api.post<{ valid: boolean; errors?: string[] }>('/policies/validate', {
      yaml: yamlContent,
    });
    return data;
  },
};

export const taskApi = {
  async submit(description: string, priority = 'medium', context?: Record<string, unknown>): Promise<{ taskId: string }> {
    const { data } = await api.post<{ taskId: string }>('/tasks', { description, priority, context });
    return data;
  },

  async list(): Promise<{ tasks: unknown[]; stats: { total: number; queued: number; executing: number; completed: number; failed: number } }> {
    const { data } = await api.get('/tasks');
    return data;
  },

  async get(taskId: string): Promise<unknown> {
    const { data } = await api.get(`/tasks/${taskId}`);
    return data;
  },

  async cancel(taskId: string): Promise<{ success: boolean }> {
    const { data } = await api.post<{ success: boolean }>(`/tasks/${taskId}/cancel`);
    return data;
  },

  async approveStep(stepId: string, approved = true): Promise<void> {
    await api.post(`/tasks/approve/${stepId}`, { approved });
  },
};

export const memoryApi = {
  async get(): Promise<unknown> {
    const { data } = await api.get('/memory');
    return data;
  },

  async setOrgContext(key: string, value: string): Promise<void> {
    await api.post('/memory/org', { key, value });
  },
};

export default api;

